/**
 * Client-side error reporting → POST /api/v1/errorreport/v1/ingest
 * Uses fetch (not axios) so failures never re-enter the axios interceptor loop.
 */
import { getUsageSessionId } from './usageTracking'

const INGEST_URL = '/api/v1/errorreport/v1/ingest'
const MAX_SNIPPET = 2000

let storeRef = null
let routerRef = null
let circuitOpen = false
let recentRoutes = []
let started = false

function truncate(s, max = MAX_SNIPPET) {
  const str = String(s || '')
  if (str.length <= max) return str
  return `${str.slice(0, max)}…`
}

function safeJson(value) {
  try {
    return truncate(typeof value === 'string' ? value : JSON.stringify(value))
  } catch {
    return ''
  }
}

/** Extract HTTP failure metadata from an axios-style error. */
export function httpContextFromError(err) {
  if (!err) return undefined
  const status = err.response?.status
  const method = err.config?.method ? String(err.config.method).toUpperCase() : ''
  const url = err.config?.url || err.config?.baseURL || ''
  const base = err.config?.baseURL || ''
  let path = url
  if (base && url && !url.startsWith('http') && !url.startsWith('/api')) {
    path = `${base.replace(/\/$/, '')}/${String(url).replace(/^\//, '')}`
  }
  return {
    method,
    url: path,
    status: status || 0,
    bodySnippet: safeJson(err.response?.data) || truncate(err.message || ''),
  }
}

/**
 * Session expiry / auth redirects are expected — do not email ops for them.
 * Covers axios 401s and best-effort auth calls (e.g. timezone sync).
 * @param {{ error?: any, http?: { status?: number, url?: string }, message?: string }} opts
 */
export function isExpectedAuthNoise(opts = {}) {
  const httpCtx = opts.http || httpContextFromError(opts.error)
  const status = Number(httpCtx?.status || opts.error?.response?.status || 0)
  if (status === 401 || status === 403) return true

  const path = String(httpCtx?.url || opts.error?.config?.url || '')
  if (path.includes('/auth/v1/')) return true

  const message = String(opts.message || opts.error?.message || '')
  if (/status code 401/i.test(message) || /status code 403/i.test(message)) return true

  return false
}

function currentRoute() {
  try {
    const r = routerRef?.currentRoute?.value
    if (!r) return window.location?.pathname || ''
    return r.fullPath || r.path || ''
  } catch {
    return typeof window !== 'undefined' ? window.location.pathname : ''
  }
}

function buildPayload({ kind, message, error, http, extra }) {
  const stack = (error && (error.stack || error.message)) || (typeof error === 'string' ? error : '') || ''
  const httpCtx = http || httpContextFromError(error)
  const user = storeRef?.state?.user
  const company = storeRef?.state?.company
  const payload = {
    source: 'frontend',
    kind: kind || 'frontend_error',
    message: String(message || error?.message || kind || 'Unknown error'),
    stack: truncate(stack, MAX_SNIPPET * 2),
    pageURL: typeof window !== 'undefined' ? window.location.href : '',
    route: currentRoute(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    usageSessionId: getUsageSessionId(),
    extra: {
      ...(extra || {}),
    },
  }
  if (user?.id) payload.extra.clientUserId = String(user.id)
  if (user?.email) payload.extra.clientUserEmail = String(user.email)
  if (user?.name) payload.extra.clientUserName = String(user.name)
  if (company?.id) payload.extra.clientCompanyId = String(company.id)
  if (company?.name) payload.extra.clientCompanyName = String(company.name)
  if (recentRoutes.length) {
    payload.extra.recentRoutes = recentRoutes.slice(-5).join(' → ')
  }
  if (httpCtx) {
    payload.httpMethod = httpCtx.method || ''
    payload.httpPath = httpCtx.url || ''
    payload.httpStatus = httpCtx.status || 0
    payload.responseSnippet = httpCtx.bodySnippet || ''
  }
  return payload
}

/**
 * Report an error to ops email (deduped server-side). Never throws / never snackbars.
 * @param {{ kind?: string, message?: string, error?: any, http?: object, extra?: Record<string,string> }} opts
 */
export async function reportError(opts = {}) {
  if (circuitOpen || typeof window === 'undefined') return
  if (isExpectedAuthNoise(opts)) return
  const payload = buildPayload(opts)
  if (!payload.message && !payload.kind) return
  try {
    const res = await fetch(INGEST_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok && res.status >= 500) {
      circuitOpen = true
    }
  } catch {
    circuitOpen = true
  }
}

/**
 * @param {{ router?: import('vue-router').Router, store?: import('vuex').Store }} opts
 */
export function startErrorReporting({ router, store } = {}) {
  if (started || typeof window === 'undefined') return
  started = true
  storeRef = store || null
  routerRef = router || null

  if (router && typeof router.afterEach === 'function') {
    router.afterEach((to) => {
      recentRoutes.push(to.fullPath || to.path)
      if (recentRoutes.length > 8) recentRoutes = recentRoutes.slice(-8)
    })
  }
}

export function installGlobalErrorHandlers(app) {
  if (typeof window === 'undefined') return

  if (app?.config) {
    app.config.errorHandler = (err, instance, info) => {
      reportError({
        kind: 'vue_error',
        message: err?.message || String(err),
        error: err,
        extra: { vueInfo: String(info || '') },
      })
      // eslint-disable-next-line no-console
      console.error(err)
    }
  }

  window.addEventListener('error', (event) => {
    reportError({
      kind: 'window_onerror',
      message: event.message || 'window.error',
      error: event.error || event.message,
      extra: {
        filename: String(event.filename || ''),
        lineno: String(event.lineno || ''),
        colno: String(event.colno || ''),
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const http = httpContextFromError(reason)
    if (isExpectedAuthNoise({ error: reason, http, message: reason?.message })) return
    reportError({
      kind: 'unhandledrejection',
      message: reason?.message || String(reason || 'unhandledrejection'),
      error: reason,
      http,
    })
  })
}
